import { FilterType } from './atoms';
const { baseUrl } = window.conviva;

// Helper function to clean up old preset data from localStorage
export const cleanupOldPresetData = (prefix: string) => {
  const keysToRemove: string[] = [];

  // Find all keys with the given prefix
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  // Remove all found keys
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

// Clean up all preset data from localStorage
export const cleanupAllPresetData = () => {
  cleanupOldPresetData('flow_preset_data_');
  cleanupOldPresetData('frustration_signal_preset_data_');
  cleanupOldPresetData('event_preset_data_');
};

// Helper function to generate the flow preset URL (without creating localStorage entry)
export const generateFlowPresetUrl = ({ flowMeta }) => {
  if (!flowMeta || !flowMeta.id) {
    return null;
  }

  // Generate a placeholder URL without storing data
  // The actual storage will happen when user clicks the link
  const url = `/dashboard/preset/flow/${String(flowMeta.id)}`;
  return baseUrl + url;
};

// Helper function to generate flow preset URL with storage key (for right-click/new tab scenarios)
export const generateFlowPresetUrlWithStorage = ({ flowMeta, appliedFilter, appliedInterval }) => {
  if (!flowMeta || !flowMeta.id) {
    return null;
  }

  const flowPresetData: {
    flowId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    flowId: String(flowMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    flowPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    flowPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `flow_preset_data_${Date.now()}`;
  localStorage.setItem(storageKey, JSON.stringify(flowPresetData));

  const url = `/dashboard/preset/flow/${String(flowMeta.id)}?flowPresetKey=${storageKey}`;
  return baseUrl + url;
};

export const openFlowPreset = ({ flowMeta, appliedFilter, appliedInterval, navigate }) => {
  if (!flowMeta || !flowMeta.id) {
    return;
  }

  const flowPresetData: {
    flowId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    flowId: String(flowMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    flowPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    flowPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `flow_preset_data_${Date.now()}`;

  localStorage.setItem(storageKey, JSON.stringify(flowPresetData));

  const url = `/dashboard/preset/flow/${String(flowMeta.id)}?flowPresetKey=${storageKey}`;
  const fullUrl = baseUrl + url;
  if (navigate) {
    navigate(fullUrl);
  } else {
    window.location.assign(fullUrl);
  }
};

// Helper function to generate the frustration signal preset URL (without creating localStorage entry)
export const generateFrustrationSignalPresetUrl = ({ signalMeta }) => {
  if (!signalMeta || !signalMeta.id) {
    return null;
  }

  // Generate a placeholder URL without storing data
  // The actual storage will happen when user clicks the link
  const url = `/dashboard/preset/frustration-signal/${String(signalMeta.id)}`;
  return baseUrl + url;
};

// Helper function to generate frustration signal preset URL with storage key (for right-click/new tab scenarios)
export const generateFrustrationSignalPresetUrlWithStorage = ({
  signalMeta,
  appliedFilter,
  appliedInterval,
}) => {
  if (!signalMeta || !signalMeta.id) {
    return null;
  }

  const frustrationSignalPresetData: {
    frustrationSignalId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    frustrationSignalId: String(signalMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    frustrationSignalPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    frustrationSignalPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `frustration_signal_preset_data_${Date.now()}`;
  localStorage.setItem(storageKey, JSON.stringify(frustrationSignalPresetData));

  const url = `/dashboard/preset/frustration-signal/${String(signalMeta.id)}?frustrationSignalPresetKey=${storageKey}`;
  return baseUrl + url;
};

export const openFrustrationSignalPreset = ({
  signalMeta,
  appliedFilter,
  appliedInterval,
  navigate,
}) => {
  if (!signalMeta || !signalMeta.id) {
    return;
  }

  const frustrationSignalPresetData: {
    frustrationSignalId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    frustrationSignalId: String(signalMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    frustrationSignalPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    frustrationSignalPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `frustration_signal_preset_data_${Date.now()}`;

  localStorage.setItem(storageKey, JSON.stringify(frustrationSignalPresetData));

  const url = `/dashboard/preset/frustration-signal/${String(signalMeta.id)}?frustrationSignalPresetKey=${storageKey}`;
  const fullUrl = baseUrl + url;
  if (navigate) {
    navigate(fullUrl);
  } else {
    window.location.assign(fullUrl);
  }
};

// Helper function to generate the event preset URL (without creating localStorage entry)
export const generateEventsPresetUrl = ({ eventMeta }) => {
  if (!eventMeta || !eventMeta.id) {
    return null;
  }

  const url = `/dashboard/preset/event/${String(eventMeta.id)}`;
  return baseUrl + url;
};

// Helper function to generate event preset URL with storage key (for right-click/new tab scenarios)
export const generateEventsPresetUrlWithStorage = ({
  eventMeta,
  appliedFilter,
  appliedInterval,
}) => {
  if (!eventMeta || !eventMeta.id) {
    return null;
  }

  const eventPresetData: {
    eventId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    eventId: String(eventMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    eventPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    eventPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `event_preset_data_${Date.now()}`;
  localStorage.setItem(storageKey, JSON.stringify(eventPresetData));

  const url = `/dashboard/preset/event/${String(eventMeta.id)}?eventPresetKey=${storageKey}`;
  return baseUrl + url;
};

export const openEventsPreset = ({ eventMeta, appliedFilter, appliedInterval, navigate }) => {
  if (!eventMeta || !eventMeta.id) {
    return;
  }

  const eventsPresetData: {
    eventId: string;
    filter?: FilterType;
    interval?: {
      from: string | moment.Moment;
      to: string | moment.Moment;
      shortcut: string;
      granularity?: string;
    };
  } = {
    eventId: String(eventMeta.id),
  };

  if (appliedFilter && appliedFilter.rule) {
    eventsPresetData.filter = appliedFilter;
  }

  if (appliedInterval && appliedInterval.from && appliedInterval.to) {
    eventsPresetData.interval = {
      from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
      to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
      shortcut: appliedInterval.shortcut,
      granularity: appliedInterval.granularity,
    };
  }

  const storageKey = `event_preset_data_${Date.now()}`;
  localStorage.setItem(storageKey, JSON.stringify(eventsPresetData));

  const url = `/dashboard/preset/event/${String(eventMeta.id)}?eventPresetKey=${storageKey}`;

  const fullUrl = baseUrl + url;
  if (navigate) {
    navigate(fullUrl);
  } else {
    window.location.assign(fullUrl);
  }
};
